import { useEffect, useRef } from 'react';

/**
 * Body scroll lock + visualViewport sync for portaled fullscreen overlays (ImageModal, VideoModal).
 * Fixes iOS/iPad: background scroll bleed, URL-bar resize jumps, split-screen misalignment.
 */
export function useFullscreenOverlay(show) {
  const backdropRef = useRef(null);

  useEffect(() => {
    if (!show) return;

    const scrollY = window.scrollY;
    const prev = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    const preventPageScroll = (e) => {
      const el = backdropRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      e.preventDefault();
    };

    document.addEventListener('touchmove', preventPageScroll, { passive: false });

    return () => {
      document.removeEventListener('touchmove', preventPageScroll);
      document.body.style.overflow = prev.overflow;
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const el = backdropRef.current;
    if (!el) return;

    let rafId = 0;

    const syncViewport = () => {
      const vv = window.visualViewport;
      if (vv) {
        const browserZoomed = Math.abs(vv.scale - 1) > 0.02;
        const top = browserZoomed ? 0 : vv.offsetTop;
        const left = browserZoomed ? 0 : vv.offsetLeft;
        const width = browserZoomed ? window.innerWidth : vv.width;
        const height = browserZoomed ? window.innerHeight : vv.height;

        el.style.top = `${top}px`;
        el.style.left = `${left}px`;
        el.style.width = `${width}px`;
        el.style.height = `${height}px`;
        el.style.setProperty('--modal-vv-w', `${width}px`);
        el.style.setProperty('--modal-vv-h', `${height}px`);
      } else {
        el.style.top = '0';
        el.style.left = '0';
        el.style.width = '100%';
        el.style.height = '100%';
        el.style.setProperty('--modal-vv-w', `${window.innerWidth}px`);
        el.style.setProperty('--modal-vv-h', `${window.innerHeight}px`);
      }
    };

    const scheduleSync = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(syncViewport);
    };

    syncViewport();
    requestAnimationFrame(syncViewport);

    const vv = window.visualViewport;
    vv?.addEventListener('resize', scheduleSync);
    vv?.addEventListener('scroll', scheduleSync);
    window.addEventListener('resize', scheduleSync);
    window.addEventListener('orientationchange', scheduleSync);

    return () => {
      cancelAnimationFrame(rafId);
      vv?.removeEventListener('resize', scheduleSync);
      vv?.removeEventListener('scroll', scheduleSync);
      window.removeEventListener('resize', scheduleSync);
      window.removeEventListener('orientationchange', scheduleSync);
      el.style.top = '';
      el.style.left = '';
      el.style.width = '';
      el.style.height = '';
      el.style.removeProperty('--modal-vv-w');
      el.style.removeProperty('--modal-vv-h');
    };
  }, [show]);

  return backdropRef;
}
